import React, { useState, useEffect } from "react";
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  CircularProgress, Alert, Chip, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  IconButton, Tooltip, Badge,
} from "@mui/material";
import TuneRoundedIcon          from "@mui/icons-material/TuneRounded";
import SaveRoundedIcon          from "@mui/icons-material/SaveRounded";
import RefreshRoundedIcon       from "@mui/icons-material/RefreshRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import LocalOfferRoundedIcon    from "@mui/icons-material/LocalOfferRounded";
import StarsRoundedIcon         from "@mui/icons-material/StarsRounded";
import FlagRoundedIcon          from "@mui/icons-material/FlagRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import StarRoundedIcon          from "@mui/icons-material/StarRounded";
import TimerRoundedIcon         from "@mui/icons-material/TimerRounded";
import PeopleRoundedIcon        from "@mui/icons-material/PeopleRounded";
import ScheduleRoundedIcon      from "@mui/icons-material/ScheduleRounded";
import FitnessCenterRoundedIcon from "@mui/icons-material/FitnessCenterRounded";
import WarningAmberRoundedIcon  from "@mui/icons-material/WarningAmberRounded";
import { getData, putData, API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt:"#FFF0E0",
  indigo:  "#4F46E5", indigoLt:"#EEF2FF",
  amber:   "#D97706", amberLt: "#FEF3C7",
  emerald: "#059669", emeraldLt:"#D1FAE5",
  rose:    "#E11D48", roseLt:  "#FFE4E6",
  violet:  "#7C3AED", violetLt:"#EDE9FE",
  bg:      "#F7FAFC", border:  "#E2E8F0",
  text:    "#0F172A", muted:   "#64748B",
};

const CATEGORY_META = {
  first_booking:  { title:"First-booking Discount",        sub:"DIM-A2 — default ₹ discount for new customers",              icon:<LocalOfferRoundedIcon />,    iconBg:T.orangeLt,  iconColor:T.orange,  accent:T.orange  },
  loyalty:        { title:"Loyalty Tiers",                  sub:"DIM-A3 — Gold / Platinum thresholds and discount caps",       icon:<StarsRoundedIcon />,         iconBg:T.amberLt,   iconColor:T.amber,   accent:T.amber   },
  milestone:      { title:"Trip Milestones",                sub:"DIM-B1 — per-milestone cashback bands",                       icon:<FlagRoundedIcon />,           iconBg:T.tealLt,    iconColor:T.teal,    accent:T.teal    },
  capacity_fill:  { title:"Capacity-fill Bonus",            sub:"DIM-B2 — incentive when truck fills above threshold",         icon:<LocalShippingRoundedIcon />,  iconBg:T.indigoLt,  iconColor:T.indigo,  accent:T.indigo  },
  rating_reward:  { title:"Rating Reward",                  sub:"DIM-B3 — bonus for high driver / owner ratings",              icon:<StarRoundedIcon />,           iconBg:T.amberLt,   iconColor:"#B45309", accent:"#B45309" },
  early_delivery: { title:"Early-delivery Bonus",           sub:"DIM-B4 — payout when delivery finishes before ETA",          icon:<TimerRoundedIcon />,          iconBg:T.emeraldLt, iconColor:T.emerald, accent:T.emerald },
  referral_limits:{ title:"Referral Fraud Caps",            sub:"Limits that guard against self-referral abuse",               icon:<PeopleRoundedIcon />,         iconBg:T.violetLt,  iconColor:T.violet,  accent:T.violet  },
  cron:           { title:"Cron Schedules",                 sub:"Backend job timings — restart required after changes",        icon:<ScheduleRoundedIcon />,       iconBg:T.roseLt,    iconColor:T.rose,    accent:T.rose    },
  heavy_cargo:    { title:"Heavy Cargo Threshold",          sub:"HCT-001 — weight above which HCT surcharge applies",         icon:<FitnessCenterRoundedIcon />,  iconBg:T.tealLt,    iconColor:T.tealDk,  accent:T.tealDk  },
};

const CATEGORY_ORDER = [
  "first_booking","loyalty","milestone","capacity_fill",
  "rating_reward","early_delivery","referral_limits","cron","heavy_cargo",
];

// ── Section card ──────────────────────────────────────────────────────────
const SCard = ({ accent = T.teal, children }) => (
  <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden",mb:2 }}>
    <Box sx={{ height:3,background:`linear-gradient(90deg,${accent},${accent}66)` }} />
    <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>{children}</CardContent>
  </Card>
);

// ── Main component ────────────────────────────────────────────────────────
export default function DimConfig() {
  const [grouped,         setGrouped]         = useState({});
  const [edits,           setEdits]           = useState({});
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [success,         setSuccess]         = useState(null);
  const [error,           setError]           = useState(null);
  const [confirmDimToggle,setConfirmDimToggle]= useState(null);
  const [togglingDim,     setTogglingDim]     = useState(false);

  const load = async () => {
    setLoading(true); setError(null); setSuccess(null);
    try {
      const res = await getData(API_ENDPOINTS.dimConfig);
      setGrouped(res?.result?.config || {}); setEdits({});
    } catch(e) { setError(e.response?.data?.message || "Failed to load config"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setEdit = (key, value) => setEdits(p => ({ ...p, [key]: value }));

  const save = async () => {
    const updates = Object.entries(edits).map(([key, value]) => {
      const row = Object.values(grouped).flat().find(r => r.key === key);
      let v = value;
      if (row?.data_type === "json") {
        try { v = JSON.parse(value); }
        catch(_) { return { key, _err: "Invalid JSON" }; }
      } else if (["number","percent","currency"].includes(row?.data_type)) {
        const n = Number(value);
        if (!Number.isFinite(n)) return { key, _err: "Not a number" };
        v = n;
      }
      return { key, value: v };
    });
    const failed = updates.filter(u => u._err);
    if (failed.length) { setError(`Cannot save: ${failed.map(f=>`${f.key} (${f._err})`).join(", ")}`); return; }
    if (!updates.length) { setError("No changes to save"); return; }
    setSaving(true); setError(null); setSuccess(null);
    try {
      const res = await putData(API_ENDPOINTS.dimConfig, { updates });
      const failedSrv = (res?.result?.results || []).filter(r => !r.ok);
      if (failedSrv.length) setError(`Some keys failed: ${failedSrv.map(f=>`${f.key} (${f.reason})`).join(", ")}`);
      else setSuccess(`${updates.length} key${updates.length === 1 ? "" : "s"} updated.`);
      await load();
    } catch(e) { setError(e.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const dimEnabledRow = (grouped.system || []).find(r => r.key === "dim_enabled");
  const dimEnabled    = dimEnabledRow ? Boolean(dimEnabledRow.value) : true;

  const submitDimToggle = async (next) => {
    setTogglingDim(true); setError(null); setSuccess(null);
    try {
      await putData("/api/dim/admin/config/dim-enabled", { enabled: next });
      setSuccess(`DIM module ${next ? "enabled" : "disabled"}.`);
      await load();
    } catch(e) { setError(e.response?.data?.message || "Toggle failed"); }
    finally { setTogglingDim(false); setConfirmDimToggle(null); }
  };

  const pendingCount = Object.keys(edits).length;

  if (loading) return (
    <Box sx={{ display:"flex",justifyContent:"center",alignItems:"center",minHeight:"50vh" }}>
      <CircularProgress sx={{ color:T.teal }} />
    </Box>
  );

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero header ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 55%,#005559 100%)`,
        borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex",alignItems:"center",gap:2 }}>
          <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <TuneRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Configuration
            </Typography>
            <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>
              DIM-001 · <code style={{ bgcolor:"rgba(255,255,255,0.15)", padding:"1px 5px", borderRadius:4, fontSize:12 }}>syt_dim_config</code>
              {" "}· changes apply immediately (cron jobs need a restart)
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display:"flex",gap:1.5,flexWrap:"wrap" }}>
          <Tooltip title="Reload config from database">
            <Button variant="outlined" startIcon={<RefreshRoundedIcon sx={{ fontSize:16 }} />}
              onClick={load}
              sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,color:"#fff",borderColor:"rgba(255,255,255,0.4)",bgcolor:"rgba(255,255,255,0.1)","&:hover":{ bgcolor:"rgba(255,255,255,0.2)" } }}>
              Reload
            </Button>
          </Tooltip>
          <Badge badgeContent={pendingCount} color="warning" invisible={pendingCount === 0}>
            <Button variant="contained" disableElevation
              startIcon={<SaveRoundedIcon sx={{ fontSize:16 }} />}
              onClick={save}
              disabled={saving || pendingCount === 0}
              sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,
                bgcolor:"rgba(255,255,255,0.22)",border:"1px solid rgba(255,255,255,0.4)",
                "&:hover":{ bgcolor:"rgba(255,255,255,0.35)" },
                "&.Mui-disabled":{ bgcolor:"rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.4)" } }}>
              {saving ? "Saving…" : `Save Changes${pendingCount > 0 ? ` (${pendingCount})` : ""}`}
            </Button>
          </Badge>
        </Box>
      </Box>

      {/* ── Status alerts ── */}
      {success && <Alert severity="success" variant="filled" onClose={()=>setSuccess(null)}
        sx={{ borderRadius:"12px",mb:2 }}>{success}</Alert>}
      {error && <Alert severity="error" variant="filled" onClose={()=>setError(null)}
        sx={{ borderRadius:"12px",mb:2 }}>{error}</Alert>}

      {/* ── Master switch panel ── */}
      {dimEnabledRow && (
        <Card sx={{
          borderRadius:"16px", overflow:"hidden", mb:2,
          border:`2px solid ${dimEnabled ? T.emerald : T.rose}`,
          boxShadow: dimEnabled
            ? "0 4px 20px rgba(5,150,105,0.12)"
            : "0 4px 20px rgba(225,29,72,0.12)",
        }}>
          <Box sx={{ height:4, background: dimEnabled
            ? `linear-gradient(90deg,${T.emerald},${T.emeraldLt})`
            : `linear-gradient(90deg,${T.rose},${T.roseLt})` }} />
          <CardContent sx={{
            p:2.5,"&:last-child":{ pb:2.5 },
            bgcolor: dimEnabled ? "#F0FDF4" : "#FFF1F2",
          }}>
            <Box sx={{ display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:2 }}>
              <Box sx={{ display:"flex",alignItems:"center",gap:2 }}>
                <Box sx={{ width:44,height:44,borderRadius:"12px",
                  bgcolor: dimEnabled ? T.emeraldLt : T.roseLt,
                  display:"flex",alignItems:"center",justifyContent:"center" }}>
                  <PowerSettingsNewRoundedIcon sx={{ fontSize:22,color: dimEnabled ? T.emerald : T.rose }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize:16,fontWeight:800,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                    DIM module is{" "}
                    <Box component="span" sx={{ color: dimEnabled ? T.emerald : T.rose }}>
                      {dimEnabled ? "ENABLED" : "DISABLED"}
                    </Box>
                  </Typography>
                  <Typography sx={{ fontSize:13,color:T.muted,mt:0.2,maxWidth:560,lineHeight:1.5 }}>
                    {dimEnabledRow.description}
                  </Typography>
                </Box>
              </Box>
              <Switch checked={dimEnabled} disabled={togglingDim}
                onChange={e=>setConfirmDimToggle(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked":{ color: T.emerald },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":{ bgcolor: T.emerald },
                }} />
            </Box>
            {!dimEnabled && (
              <Alert severity="warning" icon={<WarningAmberRoundedIcon />}
                sx={{ mt:2,borderRadius:"12px",fontSize:13 }}>
                While disabled: DIM endpoints return 503, discount UI is hidden on mobile,
                no incentives fire, and cron jobs are unregistered.{" "}
                <strong>Restart backend after re-enabling</strong> to re-register cron jobs.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Category sections ── */}
      {dimEnabled && CATEGORY_ORDER.map(cat => {
        const items = grouped[cat] || [];
        if (!items.length) return null;
        const meta = CATEGORY_META[cat] || { title:cat, icon:<TuneRoundedIcon />, iconBg:T.tealLt, iconColor:T.teal, accent:T.teal };
        const catDirty = items.some(r => edits[r.key] !== undefined);

        return (
          <SCard key={cat} accent={meta.accent}>
            {/* Section header */}
            <Box sx={{ display:"flex",alignItems:"center",justifyContent:"space-between",mb:2 }}>
              <Box sx={{ display:"flex",alignItems:"center",gap:1.5 }}>
                <Box sx={{ width:36,height:36,borderRadius:"10px",bgcolor:meta.iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                  {React.cloneElement(meta.icon, { sx:{ fontSize:18,color:meta.iconColor } })}
                </Box>
                <Box>
                  <Box sx={{ display:"flex",alignItems:"center",gap:1 }}>
                    <Typography sx={{ fontSize:14,fontWeight:800,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                      {meta.title}
                    </Typography>
                    {catDirty && (
                      <Chip label="Edited" size="small"
                        sx={{ height:18,fontSize:10,fontWeight:700,bgcolor:T.amberLt,color:T.amber,border:`1px solid ${T.amber}44` }} />
                    )}
                  </Box>
                  {meta.sub && <Typography sx={{ fontSize:12,color:T.muted,mt:0.1 }}>{meta.sub}</Typography>}
                </Box>
              </Box>
            </Box>

            {/* Fields */}
            <Grid container spacing={2}>
              {items.map(r => (
                <ConfigField
                  key={r.key}
                  row={r}
                  edited={edits[r.key]}
                  onEdit={v => setEdit(r.key, v)}
                  accent={meta.accent}
                />
              ))}
            </Grid>
          </SCard>
        );
      })}

      {/* ── Toggle confirm dialog ── */}
      <Dialog open={confirmDimToggle !== null} onClose={()=>setConfirmDimToggle(null)}
        PaperProps={{ sx:{ borderRadius:"20px",overflow:"hidden" } }}>
        <Box sx={{ height:4,background:`linear-gradient(90deg,${confirmDimToggle ? T.emerald : T.rose},${confirmDimToggle ? T.emeraldLt : T.roseLt})` }} />
        <DialogTitle sx={{ fontWeight:800,fontFamily:"'Plus Jakarta Sans',sans-serif",display:"flex",alignItems:"center",gap:1.5 }}>
          <Box sx={{ width:34,height:34,borderRadius:"9px",bgcolor:confirmDimToggle ? T.emeraldLt : T.roseLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
            <PowerSettingsNewRoundedIcon sx={{ fontSize:18,color:confirmDimToggle ? T.emerald : T.rose }} />
          </Box>
          {confirmDimToggle ? "Enable DIM module?" : "Disable DIM module?"}
        </DialogTitle>
        <DialogContent sx={{ pb:1 }}>
          {confirmDimToggle ? (
            /* ── ENABLE dialog body ── */
            <Box sx={{ display:"flex",flexDirection:"column",gap:1.5 }}>
              <Typography sx={{ fontSize:13,color:T.muted,lineHeight:1.6 }}>
                Re-enables every DIM surface across the platform immediately.
              </Typography>
              {/* What gets re-enabled */}
              <Box sx={{ bgcolor:T.emeraldLt,borderRadius:"12px",p:1.5,border:`1px solid ${T.emerald}33` }}>
                <Typography sx={{ fontSize:12,fontWeight:700,color:T.emerald,mb:1,textTransform:"uppercase",letterSpacing:"0.3px" }}>
                  What re-activates
                </Typography>
                {[
                  "Customer discount endpoints (DIM-A2, A3, B1–B4)",
                  "Owner & driver incentive payouts",
                  "Referral payout processing",
                  "DIM cron job registration (needs restart)",
                ].map(item => (
                  <Box key={item} sx={{ display:"flex",alignItems:"flex-start",gap:1,mb:0.5 }}>
                    <Box sx={{ width:5,height:5,borderRadius:"50%",bgcolor:T.emerald,flexShrink:0,mt:"6px" }} />
                    <Typography sx={{ fontSize:13,color:"#065F46",lineHeight:1.5 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
              {/* Restart warning */}
              <Box sx={{ bgcolor:T.amberLt,borderRadius:"12px",p:1.5,border:`1px solid ${T.amber}33`,display:"flex",gap:1.5 }}>
                <WarningAmberRoundedIcon sx={{ fontSize:18,color:T.amber,flexShrink:0,mt:"1px" }} />
                <Typography sx={{ fontSize:13,color:"#78350F",lineHeight:1.5 }}>
                  <strong>Backend restart required</strong> — cron jobs only register at boot.
                  The module re-enables immediately, but scheduled jobs won't fire until restart.
                </Typography>
              </Box>
            </Box>
          ) : (
            /* ── DISABLE dialog body ── */
            <Box sx={{ display:"flex",flexDirection:"column",gap:1.5 }}>
              <Typography sx={{ fontSize:13,color:T.muted,lineHeight:1.6 }}>
                Disables the entire DIM module <strong>immediately</strong> across all clients and services.
              </Typography>
              {/* Consequences */}
              <Box sx={{ bgcolor:T.roseLt,borderRadius:"12px",p:1.5,border:`1px solid ${T.rose}33` }}>
                <Typography sx={{ fontSize:12,fontWeight:700,color:T.rose,mb:1,textTransform:"uppercase",letterSpacing:"0.3px" }}>
                  Immediate effects
                </Typography>
                {[
                  "DIM endpoints return 503 to all callers",
                  "Discount section hidden on mobile (next refresh)",
                  "No new incentives fire on payment or trip-end",
                  "DIM cron jobs stop being registered",
                ].map(item => (
                  <Box key={item} sx={{ display:"flex",alignItems:"flex-start",gap:1,mb:0.5 }}>
                    <Box sx={{ width:5,height:5,borderRadius:"50%",bgcolor:T.rose,flexShrink:0,mt:"6px" }} />
                    <Typography sx={{ fontSize:13,color:"#881337",lineHeight:1.5 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
              {/* Safe note */}
              <Box sx={{ bgcolor:T.amberLt,borderRadius:"12px",p:1.5,border:`1px solid ${T.amber}33`,display:"flex",gap:1.5 }}>
                <WarningAmberRoundedIcon sx={{ fontSize:18,color:T.amber,flexShrink:0,mt:"1px" }} />
                <Typography sx={{ fontSize:13,color:"#78350F",lineHeight:1.5 }}>
                  In-flight discounts already injected into{" "}
                  <Box component="code" sx={{ bgcolor:"#FDE68A",px:"4px",py:"1px",borderRadius:"4px",fontSize:12 }}>pricing_meta</Box>
                  {" "}will still be honoured at payment time.
                </Typography>
              </Box>
              {/* Re-enable note */}
              <Box sx={{ bgcolor:T.tealLt,borderRadius:"12px",p:1.5,border:`1px solid ${T.teal}33`,display:"flex",gap:1.5 }}>
                <Box sx={{ width:6,height:6,borderRadius:"50%",bgcolor:T.teal,flexShrink:0,mt:"6px" }} />
                <Typography sx={{ fontSize:13,color:T.tealDk,lineHeight:1.5 }}>
                  You can re-enable from this same screen at any time.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3,py:2.5,gap:1,borderTop:`1px solid ${T.border}` }}>
          <Button onClick={()=>setConfirmDimToggle(null)} disabled={togglingDim} variant="outlined"
            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600,borderColor:T.border,color:T.muted,flex:1 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation disabled={togglingDim}
            onClick={()=>submitDimToggle(confirmDimToggle)}
            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,flex:1,
              bgcolor: confirmDimToggle ? T.emerald : T.rose,
              "&:hover":{ bgcolor: confirmDimToggle ? "#047857" : "#be123c" } }}>
            {togglingDim ? "Working…" : confirmDimToggle ? "Enable DIM" : "Disable DIM"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Config field ──────────────────────────────────────────────────────────
function ConfigField({ row, edited, onEdit, accent = T.teal }) {
  const value  = edited !== undefined ? edited : displayValue(row);
  const dirty  = edited !== undefined;
  const isJson = row.data_type === "json";
  const isCron = row.data_type === "cron";
  const isNum  = ["number","percent","currency"].includes(row.data_type);

  if (row.data_type === "boolean") {
    return (
      <Grid item xs={12} sm={6} md={4}>
        <Box sx={{ p:1.5,borderRadius:"12px",border:`1px solid ${dirty ? accent+"66" : T.border}`,bgcolor:dirty ? accent+"08" : "#fff" }}>
          <FormControlLabel
            control={
              <Switch checked={Boolean(value)} onChange={e=>onEdit(e.target.checked)}
                sx={{ "& .MuiSwitch-switchBase.Mui-checked":{ color:accent },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":{ bgcolor:accent } }} />
            }
            label={
              <Box>
                <Box sx={{ display:"flex",alignItems:"center",gap:0.5 }}>
                  <Typography sx={{ fontSize:12,fontWeight:700,color:T.text,fontFamily:"monospace" }}>{row.key}</Typography>
                  {dirty && <Box sx={{ width:6,height:6,borderRadius:"50%",bgcolor:T.amber,flexShrink:0 }} />}
                </Box>
                <Typography sx={{ fontSize:11,color:T.muted,lineHeight:1.4,mt:0.2 }}>{row.description}</Typography>
              </Box>
            }
            sx={{ m:0, alignItems:"flex-start", gap:0.5 }}
          />
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Box sx={{ position:"relative" }}>
        <TextField
          fullWidth size="small"
          label={
            <Box component="span" sx={{ fontFamily:"monospace",fontSize:12 }}>
              {row.key}
            </Box>
          }
          type={isNum ? "number" : "text"}
          value={value}
          multiline={isJson}
          minRows={isJson ? 3 : 1}
          onChange={e=>onEdit(e.target.value)}
          InputProps={{
            startAdornment: row.data_type === "currency"
              ? <Box component="span" sx={{ pr:0.5,color:T.muted,fontSize:14 }}>₹</Box> : null,
            endAdornment:   row.data_type === "percent"
              ? <Box component="span" sx={{ pl:0.5,color:T.muted,fontSize:14 }}>%</Box> : null,
            sx: {
              borderRadius:"10px",
              fontFamily: isCron || isJson ? "monospace" : undefined,
              fontSize: isJson ? 12 : 13,
              borderColor: dirty ? accent : undefined,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": dirty ? {
              "& fieldset": { borderColor:`${accent}88`,borderWidth:1.5 },
              "&:hover fieldset": { borderColor:accent },
              "&.Mui-focused fieldset": { borderColor:accent },
            } : {},
          }}
          helperText={
            <Box component="span" sx={{ display:"flex",alignItems:"center",gap:0.5 }}>
              <Typography component="span" sx={{ fontSize:11,color:T.muted }}>{row.description}</Typography>
              {dirty && <Box component="span" sx={{ width:6,height:6,borderRadius:"50%",bgcolor:T.amber,display:"inline-block",flexShrink:0 }} />}
            </Box>
          }
        />
      </Box>
    </Grid>
  );
}

function displayValue(row) {
  if (row.data_type === "json") return JSON.stringify(row.value, null, 2);
  if (row.value == null) return "";
  return String(row.value);
}
